/**
 * BSS Organizations API Hooks
 * TanStack Query hooks for organization creation and slug validation
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EDGE_FUNCTION_HEADERS } from '@/lib/edge-functions';
import { supabase } from '@/lib/supabase';
import type {
  SlugCheckResult,
  OrganizationFormData,
  CreateOrganizationResponse,
  CreateOrganizationError,
  ApiError,
  Organization,
  CurrentOrganizationResult,
} from '@/types/bss';

// ============================================
// API FUNCTIONS
// ============================================

async function checkSlug(name: string): Promise<SlugCheckResult> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/bss-organizations/check-slug`,
    {
      method: 'POST',
      headers: {
        ...EDGE_FUNCTION_HEADERS,
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to check slug');
  }

  return response.json();
}

async function createOrganization(
  data: OrganizationFormData
): Promise<CreateOrganizationResponse> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/bss-organizations/create`,
    {
      method: 'POST',
      headers: {
        ...EDGE_FUNCTION_HEADERS,
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error: CreateOrganizationError = await response.json();

    // If 409 conflict, throw with similar organizations data
    if (response.status === 409) {
      const conflictError = new Error(error.message) as any;
      conflictError.code = 'SLUG_CONFLICT';
      conflictError.slug = error.slug;
      conflictError.similarOrganizations = error.similarOrganizations;
      throw conflictError;
    }

    throw new Error(error.error || 'Failed to create organization');
  }

  return response.json();
}

async function canCreateOrg(): Promise<{ allowed: boolean; organizationName: string | null }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/bss-organizations/can-create`,
    {
      headers: {
        ...EDGE_FUNCTION_HEADERS,
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to check allowlist');
  }

  return response.json();
}

export async function getCurrentOrganization(): Promise<CurrentOrganizationResult> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/bss-organizations/current`,
    {
      headers: {
        ...EDGE_FUNCTION_HEADERS,
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch current organization');
  }

  const data = (await response.json()) as {
    organization?: Organization | null;
    onboarding_completed_at?: string | null;
  };

  // `onboarding_completed_at` is a user_profiles column. The backend carries
  // it as a sibling of `organization`, but read it defensively from either
  // spot (sibling OR nested on the org) so a payload-shape change on the
  // Hermes side can't silently strand every user on /onboarding.
  const onboardingCompletedAt =
    data.onboarding_completed_at ??
    (data.organization as { onboarding_completed_at?: string | null } | null | undefined)
      ?.onboarding_completed_at ??
    null;

  return {
    organization: data.organization ?? null,
    onboarding_completed_at: onboardingCompletedAt,
  };
}

/**
 * Rename the caller's current organisation. Wraps the `rename_my_org` RPC,
 * which trims + validates (2..120 chars) and requires the caller to be an
 * active owner/admin of their current org. Returns the org uuid.
 */
async function renameMyOrg(name: string): Promise<string> {
  const { data, error } = await supabase.rpc('rename_my_org', { p_name: name });
  if (error) throw new Error(error.message || 'Failed to rename organisation');
  return data as string;
}

/**
 * Mark onboarding complete for the signed-in user. Wraps the
 * `complete_onboarding` RPC (sets `user_profiles.onboarding_completed_at` to
 * now() when null) and returns the timestamp. Idempotent.
 */
async function completeOnboarding(): Promise<string> {
  const { data, error } = await supabase.rpc('complete_onboarding');
  if (error) throw new Error(error.message || 'Failed to complete onboarding');
  return data as string;
}

// ============================================
// QUERY HOOKS
// ============================================

/**
 * Check whether the current user is on the org-creation allowlist.
 * Used by OrganizationCreateStep to show the waiting screen.
 */
export function useCanCreateOrg() {
  return useQuery({
    queryKey: ['can-create-org'],
    queryFn: canCreateOrg,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Check slug availability with debounce
 * Returns slug, availability, similar orgs, and suggestions
 */
export function useCheckSlug(name: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['check-slug', name],
    queryFn: () => checkSlug(name),
    enabled: options?.enabled !== false && name.length > 0,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Get user's current organization
 */
export function useCurrentOrganization() {
  return useQuery({
    queryKey: ['current-organization'],
    queryFn: getCurrentOrganization,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Create organization with first admin user.
 *
 * `onBeforeCacheUpdate`, when given, runs SYNCHRONOUSLY inside `onSuccess` —
 * before the `setQueryData` call below. This matters because `setQueryData`
 * notifies every subscriber of `['current-organization']` (e.g. the
 * onboarding wizard's escape-hatch effect) immediately, and it does so before
 * `mutateAsync()`'s own returned promise resolves back to the caller. A
 * caller that waits for that resolution to flip its own React state (e.g.
 * "this creation must land on the wizard's completion step") always loses
 * that race — the subscriber re-renders and its effects run first. Giving the
 * caller a hook to run code in the SAME synchronous tick as the cache write
 * lets React 18's automatic batching combine both state updates into one
 * commit, so dependent effects see them together instead of one render apart.
 */
export function useCreateOrganization(options?: {
  onBeforeCacheUpdate?: (data: CreateOrganizationResponse) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createOrganization,
    onSuccess: (data) => {
      options?.onBeforeCacheUpdate?.(data);
      // Seed the cache immediately with the org this call just created —
      // same shape getCurrentOrganization resolves to (`{ organization }`).
      // Without this, a caller that reads ['current-organization'] before
      // the invalidation-triggered refetch lands (e.g. the root route guard
      // on the very next navigation) can still see a stale cached `null`.
      queryClient.setQueryData(['current-organization'], { organization: data.organization });
      // Return the promise so `mutateAsync` callers can await the refetch
      // instead of racing it.
      return queryClient.invalidateQueries({
        queryKey: ['current-organization'],
      });
    },
  });
}

/**
 * Rename the caller's current organisation (free-floor onboarding: the org is
 * minted at signup, so the wizard renames it rather than creating one).
 *
 * On success it seeds `['current-organization']` with the new name in the SAME
 * synchronous tick — the org row is already cached, so only its `name` changes
 * — then invalidates so the authoritative refetch lands. Seeding first means a
 * reader on the very next navigation (e.g. the root guard) sees the renamed org
 * instead of the pre-rename cached value.
 */
export function useRenameMyOrg() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => renameMyOrg(name),
    onSuccess: (_orgId, name) => {
      queryClient.setQueryData(
        ['current-organization'],
        (old: CurrentOrganizationResult | undefined) => {
          if (!old?.organization) return old;
          return { ...old, organization: { ...old.organization, name } };
        },
      );
      return queryClient.invalidateQueries({ queryKey: ['current-organization'] });
    },
  });
}

/**
 * Mark onboarding complete for the signed-in user, then invalidate
 * `['current-organization']` so the next read carries the new
 * `onboarding_completed_at` and the root guard stops redirecting to
 * /onboarding.
 */
export function useCompleteOnboarding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => completeOnboarding(),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: ['current-organization'] });
    },
  });
}
